<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import Terminal from '$lib/components/primitives/Terminal.svelte';

	type OS = 'macos' | 'windows' | 'linux-deb' | 'linux-rpm' | 'universal';

	let selectedOS = $state<OS>('macos');
	let detectedOS = $state<OS>('macos');
	let showAllPlatforms = $state(false);

	// Detect user's OS
	onMount(() => {
		const platform = navigator.platform.toLowerCase();
		const userAgent = navigator.userAgent.toLowerCase();

		if (platform.includes('mac') || userAgent.includes('mac')) {
			selectedOS = 'macos';
			detectedOS = 'macos';
		} else if (platform.includes('win') || userAgent.includes('win')) {
			selectedOS = 'windows';
			detectedOS = 'windows';
		} else if (platform.includes('linux') || userAgent.includes('linux')) {
			// Try to detect Linux distribution
			if (userAgent.includes('ubuntu') || userAgent.includes('debian')) {
				selectedOS = 'linux-deb';
				detectedOS = 'linux-deb';
			} else if (
				userAgent.includes('fedora') ||
				userAgent.includes('rhel') ||
				userAgent.includes('centos')
			) {
				selectedOS = 'linux-rpm';
				detectedOS = 'linux-rpm';
			} else {
				selectedOS = 'linux-deb';
				detectedOS = 'linux-deb';
			}
		} else {
			// Default to universal if can't detect
			selectedOS = 'universal';
			detectedOS = 'universal';
		}
	});

	const osOptions = $derived([
		{
			id: 'macos',
			name: $_('products.cli.installation.os.macos.name'),
			icon: 'apple',
			primary: $_('products.cli.installation.os.macos.primary'),
			secondary: $_('products.cli.installation.os.macos.secondary')
		},
		{
			id: 'windows',
			name: $_('products.cli.installation.os.windows.name'),
			icon: 'monitor',
			primary: $_('products.cli.installation.os.windows.primary'),
			secondary: $_('products.cli.installation.os.windows.secondary')
		},
		{
			id: 'linux-deb',
			name: $_('products.cli.installation.os.linuxDeb.name'),
			icon: 'terminal',
			primary: $_('products.cli.installation.os.linuxDeb.primary'),
			secondary: $_('products.cli.installation.os.linuxDeb.secondary')
		},
		{
			id: 'linux-rpm',
			name: $_('products.cli.installation.os.linuxRpm.name'),
			icon: 'box',
			primary: $_('products.cli.installation.os.linuxRpm.primary'),
			secondary: $_('products.cli.installation.os.linuxRpm.secondary')
		},
		{
			id: 'universal',
			name: $_('products.cli.installation.os.universal.name'),
			icon: 'download',
			primary: $_('products.cli.installation.os.universal.primary'),
			secondary: $_('products.cli.installation.os.universal.secondary')
		}
	]);

	const getInstallCommands = (os: OS) => {
		switch (os) {
			case 'macos':
				return {
					primary: {
						title: $_('products.cli.installation.methods.macos.primary.title'),
						commands: ['brew tap heyweek/heyweek-cli', 'brew install heyweek-cli']
					},
					secondary: {
						title: $_('products.cli.installation.methods.macos.secondary.title'),
						description: $_('products.cli.installation.methods.macos.secondary.description'),
						link: 'https://github.com/heyweek/homebrew-heyweek-cli/releases'
					}
				};
			case 'windows':
				return {
					primary: {
						title: $_('products.cli.installation.methods.windows.primary.title'),
						commands: [
							'scoop bucket add heyweek https://github.com/heyweek/homebrew-heyweek-cli.git',
							'scoop install heyweek'
						]
					},
					secondary: {
						title: $_('products.cli.installation.methods.windows.secondary.title'),
						description: $_('products.cli.installation.methods.windows.secondary.description'),
						link: 'https://github.com/heyweek/homebrew-heyweek-cli/releases'
					}
				};
			case 'linux-deb':
				return {
					primary: {
						title: $_('products.cli.installation.methods.linuxDeb.primary.title'),
						commands: [
							`# ${$_('products.cli.installation.methods.linuxDeb.primary.comment1')}`,
							'wget https://github.com/heyweek/homebrew-heyweek-cli/releases/download/v1.10.1/Heyweek_1.10.1_linux_amd64.deb',
							'',
							`# ${$_('products.cli.installation.methods.linuxDeb.primary.comment2')}`,
							'sudo apt install ./Heyweek_1.10.1_linux_amd64.deb',
							`# ${$_('products.cli.installation.methods.or')}`,
							'sudo dpkg -i Heyweek_1.10.1_linux_amd64.deb'
						]
					},
					secondary: {
						title: $_('products.cli.installation.methods.linuxDeb.secondary.title'),
						commands: [
							'curl -fsSL https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | bash'
						]
					}
				};
			case 'linux-rpm':
				return {
					primary: {
						title: $_('products.cli.installation.methods.linuxRpm.primary.title'),
						commands: [
							`# ${$_('products.cli.installation.methods.linuxRpm.primary.comment1')}`,
							'wget https://github.com/heyweek/homebrew-heyweek-cli/releases/download/v1.10.1/Heyweek_1.10.1_linux_amd64.rpm',
							'',
							`# ${$_('products.cli.installation.methods.linuxRpm.primary.comment2')}`,
							'sudo dnf install Heyweek_1.10.1_linux_amd64.rpm'
						]
					},
					secondary: {
						title: $_('products.cli.installation.methods.linuxRpm.secondary.title'),
						commands: [
							'curl -fsSL https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | bash'
						]
					}
				};
			case 'universal':
				return {
					primary: {
						title: $_('products.cli.installation.methods.universal.primary.title'),
						commands: [
							`# ${$_('products.cli.installation.methods.universal.primary.comment1')}`,
							'curl -fsSL https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | bash',
							'',
							`# ${$_('products.cli.installation.methods.universal.primary.comment2')}`,
							'wget -qO- https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | bash'
						]
					},
					secondary: {
						title: $_('products.cli.installation.methods.universal.secondary.title'),
						commands: [
							`# ${$_('products.cli.installation.methods.universal.secondary.comment')}`,
							'curl -fsSL https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | BINDIR=~/local/bin bash'
						]
					}
				};
		}
	};

	const currentInstallCommands = $derived(getInstallCommands(selectedOS));

	const features = $derived([
		{
			icon: 'terminal',
			title: $_('products.cli.features.terminal.title'),
			description: $_('products.cli.features.terminal.description')
		},
		{
			icon: 'clock',
			title: $_('products.cli.features.timer.title'),
			description: $_('products.cli.features.timer.description')
		},
		{
			icon: 'folder',
			title: $_('products.cli.features.projects.title'),
			description: $_('products.cli.features.projects.description')
		},
		{
			icon: 'users',
			title: $_('products.cli.features.clients.title'),
			description: $_('products.cli.features.clients.description')
		},
		{
			icon: 'file-text',
			title: $_('products.cli.features.logs.title'),
			description: $_('products.cli.features.logs.description')
		},
		{
			icon: 'shield',
			title: $_('products.cli.features.secure.title'),
			description: $_('products.cli.features.secure.description')
		}
	]);

	const installExample = $derived(`# ${$_('products.cli.installation.example.comment1')}
brew tap heyweek/heyweek-cli
brew install heyweek-cli

# ${$_('products.cli.installation.example.comment2')}
scoop bucket add heyweek https://github.com/heyweek/homebrew-heyweek-cli.git
scoop install heyweek

# ${$_('products.cli.installation.example.comment3')}
curl -fsSL https://s3.eu-central-1.amazonaws.com/heyweek.staging.cli/install.sh | bash

# ${$_('products.cli.installation.example.comment4')}
hw version`);
</script>

<svelte:head>
	<title>{$_('products.cli.meta.title')}</title>
	<meta name="description" content={$_('products.cli.meta.description')} />
	<meta property="og:title" content="{$_('products.cli.meta.title')}" />
	<meta property="og:description" content={$_('products.cli.meta.description')} />
</svelte:head>

<Header />

<div class="product-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__grid">
				<div class="hero__content">
					<div class="hero__badge">
						<Icon name="terminal" size={16} />
						<span>{$_('products.cli.badge')}</span>
					</div>
					<h1 class="hero__title">
						{$_('products.cli.hero.title')}
					</h1>
					<p class="hero__subtitle">
						{$_('products.cli.hero.subtitle')}
					</p>
					<div class="hero__actions">
						<Button href="/docs/cli/overview" variant="primary" size="lg">
							{$_('products.cli.installation.viewDocumentation')}
						</Button>
						<Button href="#installation" variant="ghost" size="lg">
							{$_('products.cli.installation.quickInstall')}
						</Button>
					</div>
				</div>
				<div class="hero__visual">
					<Terminal code={installExample} title={$_('products.cli.installation.quickInstall')} />
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features">
		<div class="container">
			<div class="features__header">
				<h2 class="features__title">{$_('products.cli.features.title')}</h2>
				<p class="features__subtitle">{$_('products.cli.features.subtitle')}</p>
			</div>

			<div class="features__grid">
				{#each features as feature}
					<Card class="feature-card">
						<div class="feature-card__icon">
							<Icon name={feature.icon} size={24} />
						</div>
						<h3 class="feature-card__title">
							{feature.title}
						</h3>
						<p class="feature-card__description">
							{feature.description}
						</p>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Installation Section -->
	<section id="installation" class="installation">
		<div class="container">
			<div class="installation__header">
				<h2 class="installation__title">{$_('products.cli.installation.title')}</h2>
				<p class="installation__subtitle">{$_('products.cli.installation.subtitle')}</p>
			</div>

			<!-- OS Selector -->
			<div class="os-selector">
				<div class="os-selector__detected">
					{#if selectedOS === detectedOS}
						<span class="os-selector__badge">
							<Icon name="check-circle" size={16} />
							{$_('products.cli.installation.detectedOS')}
						</span>
					{/if}
				</div>

				<div class="os-selector__tabs">
					{#each osOptions as os}
						<button
							class="os-tab"
							class:os-tab--active={selectedOS === os.id}
							onclick={() => (selectedOS = os.id)}
						>
							<Icon name={os.icon} size={20} />
							<div class="os-tab__content">
								<span class="os-tab__name">{os.name}</span>
								<span class="os-tab__method">{os.primary}</span>
							</div>
						</button>
					{/each}
				</div>
			</div>

			<!-- Installation Content -->
			<div class="installation__content">
				<div class="install-method install-method--primary">
					<h3 class="install-method__title">
						{currentInstallCommands.primary.title}
						<span class="install-method__badge">{$_('products.cli.installation.recommended')}</span>
					</h3>

					<Terminal
						code={currentInstallCommands.primary.commands.join('\n')}
						title={currentInstallCommands.primary.title}
					/>

					{#if currentInstallCommands.primary.description}
						<p class="install-method__description">{currentInstallCommands.primary.description}</p>
					{/if}
				</div>

				{#if currentInstallCommands.secondary}
					<div class="install-method install-method--secondary">
						<h3 class="install-method__title">
							{currentInstallCommands.secondary.title}
							<span class="install-method__badge install-method__badge--alt"
								>{$_('products.cli.installation.alternative')}</span
							>
						</h3>

						{#if currentInstallCommands.secondary.commands}
							<Terminal
								code={currentInstallCommands.secondary.commands.join('\n')}
								title={currentInstallCommands.secondary.title}
							/>
						{/if}

						{#if currentInstallCommands.secondary.description}
							<p class="install-method__description">
								{currentInstallCommands.secondary.description}
								{#if currentInstallCommands.secondary.link}
									<a
										href={currentInstallCommands.secondary.link}
										target="_blank"
										rel="noopener"
										class="install-method__link"
									>
										{$_('products.cli.installation.viewReleases')}
										<Icon name="arrow-up-right" size={14} />
									</a>
								{/if}
							</p>
						{/if}
					</div>
				{/if}

				<!-- Verify Installation -->
				<div class="verify-section">
					<h4 class="verify-section__title">
						<Icon name="shield-check" size={20} />
						{$_('products.cli.installation.verifyInstallation')}
					</h4>
					<Terminal
						code="hw version"
						title={$_('products.cli.installation.verifyInstallation')}
						showCopy={false}
					/>
				</div>
			</div>
		</div>
	</section>

	<!-- Usage Section -->
	<section class="usage">
		<div class="container">
			<div class="usage__header">
				<h2 class="usage__title">{$_('products.cli.usage.title')}</h2>
				<p class="usage__subtitle">{$_('products.cli.usage.subtitle')}</p>
				<div class="usage__docs-link">
					<Button href="/docs/cli/overview" variant="ghost" size="sm">
						<Icon name="book-open" size={16} />
						{$_('products.cli.usage.fullDocumentation')}
					</Button>
				</div>
			</div>

			<div class="usage__grid">
				<div class="usage-example">
					<h3>{$_('products.cli.usage.auth.title')}</h3>
					<Terminal
						code={`# ${$_('products.cli.usage.auth.comment1')}
hw auth login

# ${$_('products.cli.usage.auth.comment2')}
hw auth renew

# ${$_('products.cli.usage.auth.comment3')}
hw auth logout`}
						title={$_('products.cli.usage.auth.title')}
					/>
				</div>

				<div class="usage-example">
					<h3>{$_('products.cli.usage.timer.title')}</h3>
					<Terminal
						code={`# ${$_('products.cli.usage.timer.comment1')}
hw timer start -d "${$_('products.cli.usage.timer.example')}"

# ${$_('products.cli.usage.timer.comment2')}
hw timer status

# ${$_('products.cli.usage.timer.comment3')}
hw timer stop`}
						title={$_('products.cli.usage.timer.title')}
					/>
				</div>

				<div class="usage-example">
					<h3>{$_('products.cli.usage.projects.title')}</h3>
					<Terminal
						code={`# ${$_('products.cli.usage.projects.comment1')}
hw project list

# ${$_('products.cli.usage.projects.comment2')}
hw project create --name "${$_('products.cli.usage.projects.example')}"

# ${$_('products.cli.usage.projects.comment3')}
hw project delete <project-id>`}
						title={$_('products.cli.usage.projects.title')}
					/>
				</div>

				<div class="usage-example">
					<h3>{$_('products.cli.usage.logs.title')}</h3>
					<Terminal
						code={`# ${$_('products.cli.usage.logs.comment1')}
hw log list --limit 10

# ${$_('products.cli.usage.logs.comment2')}
hw log create --description "${$_('products.cli.usage.logs.example')}"

# ${$_('products.cli.usage.logs.comment3')}
hw log delete <log-id>`}
						title={$_('products.cli.usage.logs.title')}
					/>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="products-cli" />

<Footer />

<style>
	.product-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Override Tailwind's container with proper constraints */
	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
	}

	.hero__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	.hero__visual {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		max-width: 600px;
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
	}

	.features__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.features__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
	}

	:global(.feature-card) {
		padding: var(--space-8);
		transition: all 0.2s ease;
		border: 1px solid var(--border-light);
	}

	:global(.feature-card:hover) {
		transform: translateY(-4px);
		border-color: var(--border-brand);
		box-shadow: var(--shadow-elevated);
	}

	:global(.dark .feature-card) {
		border-color: var(--border-default);
	}

	:global(.dark .feature-card:hover) {
		border-color: var(--border-brand);
	}

	.feature-card__icon {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .feature-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.feature-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.feature-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Installation Section */
	.installation {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.installation__header {
		text-align: center;
		margin-bottom: var(--space-8);
	}

	.installation__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.installation__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	/* OS Selector */
	.os-selector {
		max-width: 720px;
		margin: 0 auto var(--space-10);
	}

	.os-selector__detected {
		display: flex;
		justify-content: center;
		margin-bottom: var(--space-3);
		min-height: 20px;
	}

	.os-selector__badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 4px 10px;
		background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.12));
		color: rgb(34, 197, 94);
		border-radius: 999px;
		font-size: 12px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		animation: slideDown 0.3s ease;
		border: 1px solid rgba(34, 197, 94, 0.2);
	}

	:global(.dark) .os-selector__badge {
		background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.15));
		color: rgb(74, 222, 128);
		border-color: rgba(34, 197, 94, 0.3);
	}

	.os-selector__tabs {
		display: flex;
		gap: 0;
		padding: 4px;
		background: var(--bg-secondary);
		border-radius: 16px;
		overflow-x: auto;
		position: relative;
		scrollbar-width: none;
		border: 1px solid var(--border-light);
	}

	.os-selector__tabs::-webkit-scrollbar {
		display: none;
	}

	:global(.dark) .os-selector__tabs {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	.os-tab {
		flex: 1;
		min-width: 130px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 10px 16px;
		background: transparent;
		border: none;
		border-radius: 12px;
		cursor: pointer;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		position: relative;
		z-index: 1;
	}

	.os-tab::before {
		content: '';
		position: absolute;
		inset: 0;
		background: transparent;
		border-radius: 12px;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: -1;
	}

	.os-tab:hover:not(.os-tab--active) {
		background: var(--bg-tertiary);
		opacity: 0.7;
	}

	:global(.dark) .os-tab:hover:not(.os-tab--active) {
		background: var(--bg-secondary);
		opacity: 0.7;
	}

	.os-tab--active {
		background: var(--bg-primary);
		box-shadow:
			0 1px 3px rgba(0, 0, 0, 0.05),
			0 1px 2px rgba(0, 0, 0, 0.1);
	}

	:global(.dark) .os-tab--active {
		background: var(--bg-secondary);
		box-shadow:
			0 1px 3px rgba(0, 0, 0, 0.2),
			0 1px 2px rgba(0, 0, 0, 0.3);
	}

	.os-tab--active .os-tab__name {
		color: var(--text-primary);
		font-weight: 600;
	}

	.os-tab--active .os-tab__method {
		color: var(--text-secondary);
		opacity: 0.9;
	}

	.os-tab__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2px;
		text-align: center;
	}

	.os-tab__name {
		font-size: 13px;
		font-weight: 500;
		color: var(--text-secondary);
		white-space: nowrap;
		transition: all 0.2s ease;
		line-height: 1.2;
	}

	.os-tab__method {
		font-size: 12px;
		color: var(--text-tertiary);
		white-space: nowrap;
		opacity: 0.7;
		transition: all 0.2s ease;
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	/* Icon styling in tabs */
	.os-tab :global(.icon) {
		opacity: 0.7;
		transition: all 0.2s ease;
	}

	.os-tab--active :global(.icon) {
		opacity: 1;
		color: rgb(255, 94, 126);
	}

	:global(.dark) .os-tab--active :global(.icon) {
		color: rgb(255, 140, 160);
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* Installation Content */
	.installation__content {
		max-width: 800px;
		margin: 0 auto;
		animation: fadeIn 0.3s ease;
	}

	.install-method {
		margin-bottom: var(--space-8);
	}

	.install-method__title {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.install-method__badge {
		padding: var(--space-1) var(--space-2);
		background: rgba(34, 197, 94, 0.1);
		color: rgb(34, 197, 94);
		border-radius: var(--radius-md);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
	}

	:global(.dark) .install-method__badge {
		background: rgba(34, 197, 94, 0.15);
		color: rgb(74, 222, 128);
	}

	.install-method__badge--alt {
		background: rgba(156, 163, 175, 0.1);
		color: rgb(107, 114, 128);
	}

	:global(.dark) .install-method__badge--alt {
		background: rgba(156, 163, 175, 0.15);
		color: rgb(156, 163, 175);
	}

	.install-method__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-top: var(--space-3);
	}

	.install-method__link {
		display: inline-flex;
		align-items: center;
		gap: var(--space-1);
		margin-left: var(--space-2);
		color: var(--brand-primary);
		text-decoration: none;
		font-weight: var(--font-weight-medium);
		transition: opacity 0.2s ease;
	}

	.install-method__link:hover {
		opacity: 0.8;
	}

	/* Verify Section */
	.verify-section {
		margin-top: var(--space-12);
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .verify-section {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	.verify-section__title {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	/* Animations */
	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* Usage Section */
	.usage {
		padding: var(--space-20) 0;
	}

	.usage__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.usage__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.usage__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.usage__docs-link {
		margin-top: var(--space-4);
		display: flex;
		justify-content: center;
	}

	.usage__docs-link :global(.btn) {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
	}

	.usage__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
		gap: var(--space-8);
	}

	.usage-example {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.usage-example h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}

	/* Responsive Design */
	@media (max-width: 968px) {
		.hero__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.features__grid {
			grid-template-columns: 1fr;
		}

		.os-selector__tabs {
			flex-direction: column;
		}

		.os-tab {
			width: 100%;
		}

		.usage__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
